<template>
  <main class="contact-page">
    <h1 class="serif">{{ t('contact.title') }}</h1>
    <p class="subtitle">{{ t('contact.subtitle') }}</p>

    <form v-if="!sent" class="contact-form" @submit.prevent="onSubmit">
      <div class="field-group">
        <label for="naam">{{ t('contact.naam.label') }}</label>
        <input type="text" id="naam" v-model="naam" :placeholder="t('contact.naam.placeholder')" :class="{ invalid: errors.naam }">
        <p v-if="errors.naam" class="field-error">{{ errors.naam }}</p>
      </div>
      <div class="field-group">
        <label for="email">{{ t('contact.email.label') }}</label>
        <input type="email" id="email" v-model="email" :placeholder="t('contact.email.placeholder')" :class="{ invalid: errors.email }">
        <p v-if="errors.email" class="field-error">{{ errors.email }}</p>
      </div>
      <div class="field-group">
        <label for="bericht">{{ t('contact.bericht.label') }}</label>
        <textarea id="bericht" v-model="bericht" rows="6" :placeholder="t('contact.bericht.placeholder')" :class="{ invalid: errors.bericht }"></textarea>
        <p v-if="errors.bericht" class="field-error">{{ errors.bericht }}</p>
      </div>
      <div class="field-group" style="position:absolute;left:-9999px;opacity:0;height:0;overflow:hidden;" aria-hidden="true" tabindex="-1">
        <label for="contact-website">Website</label>
        <input type="text" id="contact-website" v-model="website" autocomplete="off" tabindex="-1">
      </div>
      <p v-if="error" class="form-error">{{ error }}</p>
      <button type="submit" class="submit-btn" :disabled="sending">
        {{ sending ? t('contact.submitting') : t('contact.submit') }}
      </button>
    </form>

    <div v-else class="success-card">
      <div class="success-icon">✓</div>
      <h2 class="serif">{{ t('contact.success.title') }}</h2>
      <p>{{ t('contact.success.text') }}</p>
      <router-link :to="isEnglish ? '/en' : '/'" class="back-btn">{{ t('contact.success.back') }}</router-link>
    </div>
  </main>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useLocale } from '../composables/useLocale'

const { t, locale, isEnglish } = useLocale()

const naam = ref('')
const email = ref('')
const bericht = ref('')
const website = ref('')
const sending = ref(false)
const sent = ref(false)
const error = ref('')
const errors = ref({})

watch(naam, () => { errors.value = { ...errors.value, naam: null } })
watch(email, () => { errors.value = { ...errors.value, email: null } })
watch(bericht, () => { errors.value = { ...errors.value, bericht: null } })

function validate() {
  const e = {}
  if (!naam.value.trim()) e.naam = t('contact.error.naam')
  if (!email.value.trim()) {
    e.email = t('contact.error.email')
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
    e.email = t('contact.error.emailInvalid')
  }
  if (!bericht.value.trim()) e.bericht = t('contact.error.bericht')
  errors.value = e
  return Object.keys(e).length === 0
}

async function onSubmit() {
  error.value = ''
  if (!validate()) return
  sending.value = true

  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        naam: naam.value.trim(),
        email: email.value.trim(),
        bericht: bericht.value.trim(),
        locale: locale.value,
        website: website.value,
      }),
    })

    if (!res.ok) {
      const data = await res.json()
      error.value = data.error || t('contact.error.submit')
      return
    }

    sent.value = true
  } catch {
    error.value = t('contact.error.submit')
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.contact-page {
  max-width: 600px;
  margin: 0 auto;
  padding: 120px 24px 80px;
}
@media (max-width: 768px) {
  .contact-page { padding: 40px 20px 48px; }
}
.contact-page h1 {
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.03em;
  margin-bottom: 16px;
}
.subtitle {
  font-size: 18px;
  color: var(--ink-soft);
  margin-bottom: 48px;
  line-height: 1.5;
}

.contact-form {
  background: var(--bg-card);
  border: 1px solid var(--line-soft);
  border-radius: 24px;
  padding: 40px;
  box-shadow: var(--shadow-soft);
}
@media (max-width: 600px) {
  .contact-form { padding: 24px; border-radius: 20px; }
}

.field-group {
  margin-bottom: 24px;
}
.field-group:last-of-type {
  margin-bottom: 28px;
}
.field-group label {
  display: block;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 8px;
  color: var(--ink);
}
.field-group input,
.field-group textarea {
  width: 100%;
  padding: 14px 16px;
  font-size: 17px;
  font-family: inherit;
  border: 1.5px solid var(--line-soft);
  background: var(--bg);
  border-radius: 12px;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
  color: var(--ink);
  resize: vertical;
}
.field-group input:focus,
.field-group textarea:focus {
  background: var(--bg-card);
  border-color: var(--blue);
  box-shadow: 0 0 0 3px rgba(31, 118, 214, 0.15);
}

.submit-btn {
  width: 100%;
  background: var(--blue);
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  padding: 18px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  box-shadow: var(--shadow-soft);
}
.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover);
  background: var(--blue-dark);
}
.submit-btn:disabled {
  background: var(--ink-muted);
  cursor: wait;
  transform: none;
  box-shadow: none;
}

.field-error {
  color: #c0392b;
  font-size: 13px;
  margin-top: 6px;
  font-weight: 600;
}
.field-group input.invalid,
.field-group textarea.invalid {
  border-color: #c0392b;
  background: #fdf2f2;
}
.form-error {
  color: #c0392b;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
}

.success-card {
  background: var(--bg-card);
  border: 1px solid var(--line-soft);
  border-radius: 24px;
  padding: 56px 40px;
  text-align: center;
  box-shadow: var(--shadow-soft);
}
.success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--blue);
  color: #fff;
  font-size: 28px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}
.success-card h2 {
  font-size: clamp(24px, 4vw, 32px);
  font-weight: 800;
  margin-bottom: 12px;
  color: var(--ink);
}
.success-card p {
  font-size: 16px;
  color: var(--ink-soft);
  margin-bottom: 32px;
  line-height: 1.5;
  max-width: 40ch;
  margin-left: auto;
  margin-right: auto;
}
.back-btn {
  display: inline-block;
  background: var(--blue);
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  font-size: 16px;
  padding: 14px 28px;
  border-radius: 999px;
  transition: transform 0.2s, background 0.2s;
}
.back-btn:hover {
  transform: translateY(-2px);
  background: var(--blue-dark);
}
</style>
